import type { AccelerometerReading } from '../domain/telemetry/raw/AccelerometerReading'
import type { GyroscopeReading } from '../domain/telemetry/raw/GyroscopeReading'
import type { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { gpsSpeedTrendMs2 } from './accelConsistencyValidator'
import { computeGpsCoordinatedLeanSeries } from './gpsCoordinatedLean'
import { reduceReadingsToGpsTicks } from './imuWindowAverage'
import { smoothValuesGaussian } from '../utils/smoothingUtils'
import {
  SENSOR_CALIBRATION_MIN_SAMPLES,
  SENSOR_CALIBRATION_MIN_MEAN_G,
  SENSOR_CALIBRATION_MAX_MEAN_G,
  SENSOR_CALIBRATION_MIN_DYNAMIC_MS2,
  SENSOR_CALIBRATION_MIN_DYNAMIC_SAMPLES,
  SENSOR_CALIBRATION_MIN_FORWARD_FIT,
  SENSOR_CALIBRATION_UPRIGHT_MAX_LEAN_DEG,
  SENSOR_CALIBRATION_UPRIGHT_MEDIAN_HALF_MS,
  SENSOR_CALIBRATION_YAW_REFINE_MIN_CORRELATION,
  SENSOR_CALIBRATION_YAW_REFINE_MIN_RIDING_S,
  SENSOR_CALIBRATION_YAW_REFINE_MIN_SPEED_KMH,
  SENSOR_CALIBRATION_YAW_REFINE_SMOOTHING_S,
} from '../analysis/config/sensorFrameCalibration'

const G = 9.81

export interface SensorFrameCalibration {
  /** Row-major 3×3 rotation mapping sensor-frame vectors into the bike frame (x fwd, y right, z down; at-rest accel (0,0,−1)g). Identity when untrusted. */
  rotation: number[][]
  /** False when the data failed a trust gate; rotation is identity then. */
  trusted: boolean
  /** Angle (deg) between the sensor's gravity axis and the bike frame's (0 = aligned z-down mount, ≈180 = z-up mount). */
  tiltDeg: number
  /** Resolved forward-axis twist (deg); 0 when the forward fit was untrusted. */
  yawDeg: number
  /** True when the yaw was refined from the gyroscope; see refineYawWithGyro. */
  yawRefined: boolean
  /** Roll-rate vs GPS-lean-rate correlation at the refined yaw; null when the refinement could not run. */
  yawRefinementCorrelation: number | null
}

type Vec3 = [number, number, number]
type Mat3 = number[][]

const IDENTITY: Mat3 = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
]

const UNTRUSTED: SensorFrameCalibration = {
  rotation: IDENTITY,
  trusted: false,
  tiltDeg: 0,
  yawDeg: 0,
  yawRefined: false,
  yawRefinementCorrelation: null,
}

// --- small vector / matrix helpers (row-major) ---
const dot = (a: Vec3, b: Vec3): number =>
  a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
]
const norm = (v: Vec3): number => Math.hypot(v[0], v[1], v[2])
const scale = (v: Vec3, s: number): Vec3 => [v[0] * s, v[1] * s, v[2] * s]
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const matVec = (m: Mat3, v: Vec3): Vec3 => [
  dot(m[0] as Vec3, v),
  dot(m[1] as Vec3, v),
  dot(m[2] as Vec3, v),
]

function matMul(a: Mat3, b: Mat3): Mat3 {
  const r: Mat3 = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ]
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      for (let k = 0; k < 3; k++) r[i][j] += a[i][k] * b[k][j]
  return r
}

const transpose = (m: Mat3): Mat3 => [
  [m[0][0], m[1][0], m[2][0]],
  [m[0][1], m[1][1], m[2][1]],
  [m[0][2], m[1][2], m[2][2]],
]

const skew = (v: Vec3): Mat3 => [
  [0, -v[2], v[1]],
  [v[2], 0, -v[0]],
  [-v[1], v[0], 0],
]

const rotZ = (angleRad: number): Mat3 => {
  const c = Math.cos(angleRad)
  const s = Math.sin(angleRad)
  return [
    [c, -s, 0],
    [s, c, 0],
    [0, 0, 1],
  ]
}

const rotX = (angleRad: number): Mat3 => {
  const c = Math.cos(angleRad)
  const s = Math.sin(angleRad)
  return [
    [1, 0, 0],
    [0, c, -s],
    [0, s, c],
  ]
}

const DOWN: Vec3 = [0, 0, -1]

/**
 * Minimal (smallest-angle) rotation taking unit vector `a` onto unit vector
 * `b`, via Rodrigues' formula. Degenerate cases: when the vectors are already
 * aligned it is identity; when antiparallel it is a 180° rotation about x
 * (any axis ⊥ a would do — x is a safe choice for the near-vertical gravity
 * vectors this module handles).
 */
function minimalRotation(a: Vec3, b: Vec3): Mat3 {
  const axis = cross(a, b)
  const s = norm(axis)
  const c = dot(a, b)
  if (s < 1e-9) {
    return c > 0
      ? IDENTITY
      : [
          [1, 0, 0],
          [0, -1, 0],
          [0, 0, -1],
        ]
  }
  const k = scale(axis, 1 / s)
  const K = skew(k)
  const K2 = matMul(K, K)
  const theta = Math.atan2(s, c)
  const sinT = Math.sin(theta)
  const oneMinusCos = 1 - Math.cos(theta)
  const r: Mat3 = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ]
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      r[i][j] = (i === j ? 1 : 0) + sinT * K[i][j] + oneMinusCos * K2[i][j]
  return r
}

/**
 * For each accelerometer sample, the nearest GPS index by timestamp. Both
 * streams are assumed sorted ascending, so a single forward-only pointer walks
 * both in O(n+m). Returns an empty array when there are no GPS points.
 */
function nearestGpsIndices(
  accelerometer: AccelerometerReading[],
  gpsPoints: GpsPoint[]
): number[] {
  const out: number[] = new Array(accelerometer.length).fill(0)
  if (gpsPoints.length === 0) return out
  let gi = 0
  for (let i = 0; i < accelerometer.length; i++) {
    const t = accelerometer[i].timestamp.getTime()
    while (
      gi < gpsPoints.length - 1 &&
      Math.abs(gpsPoints[gi + 1].timestamp.getTime() - t) <=
        Math.abs(gpsPoints[gi].timestamp.getTime() - t)
    ) {
      gi++
    }
    out[i] = gi
  }
  return out
}

function centredMedian(
  values: number[],
  timesMs: number[],
  halfMs: number
): number[] {
  const out = new Array<number>(values.length).fill(NaN)
  let a = 0
  let b = 0
  for (let i = 0; i < values.length; i++) {
    while (a < i && timesMs[a] < timesMs[i] - halfMs) a++
    while (b < values.length - 1 && timesMs[b + 1] <= timesMs[i] + halfMs) b++
    const window: number[] = []
    for (let j = a; j <= b; j++) {
      if (Number.isFinite(values[j])) window.push(values[j])
    }
    if (window.length === 0) continue
    window.sort((x, y) => x - y)
    out[i] = window[Math.floor(window.length / 2)]
  }
  return out
}

/**
 * Samples allowed to set the roll of the mount: those recorded while the
 * surrounding GPS lean is small, where the bike carries no lateral force.
 * Leaned samples carry the rider's hang-off as a lateral force, and an
 * unequal left/right time budget folds it into the whole-ride mean. Every
 * sample qualifies when too few do.
 */
function uprightMask(
  accelerometer: AccelerometerReading[],
  gpsPoints: GpsPoint[],
  gpsIdx: number[]
): boolean[] {
  const lean = centredMedian(
    computeGpsCoordinatedLeanSeries(gpsPoints),
    gpsPoints.map((p) => p.timestamp.getTime()),
    SENSOR_CALIBRATION_UPRIGHT_MEDIAN_HALF_MS
  )
  const mask = accelerometer.map((_, i) => {
    const l = lean[gpsIdx[i]]
    return (
      !Number.isFinite(l) ||
      Math.abs(l) < SENSOR_CALIBRATION_UPRIGHT_MAX_LEAN_DEG
    )
  })
  const qualifying = mask.filter(Boolean).length
  return qualifying >= SENSOR_CALIBRATION_MIN_SAMPLES
    ? mask
    : mask.map(() => true)
}

function estimateFromAccelerometer(
  accelerometer: AccelerometerReading[],
  gpsPoints: GpsPoint[]
): SensorFrameCalibration {
  const n = accelerometer.length
  if (n < SENSOR_CALIBRATION_MIN_SAMPLES) return UNTRUSTED

  // 1. Normalize to g.
  const samples: Vec3[] = accelerometer.map((r) =>
    r.unit === 'm/s²' ? [r.x / G, r.y / G, r.z / G] : [r.x, r.y, r.z]
  )

  // 2. Gravity trust gate on the raw mean.
  let m1: Vec3 = [0, 0, 0]
  for (const s of samples) m1 = add(m1, s)
  m1 = scale(m1, 1 / n)
  const mag1 = norm(m1)
  if (
    mag1 < SENSOR_CALIBRATION_MIN_MEAN_G ||
    mag1 > SENSOR_CALIBRATION_MAX_MEAN_G
  )
    return UNTRUSTED

  // 3. First gravity pass.
  const u1 = scale(m1, 1 / mag1)
  const R1 = minimalRotation(u1, DOWN)

  // 4. GPS de-dynamicization. The raw mean absorbs real longitudinal
  //    acceleration (constant or asymmetric accel/brake biases the gravity
  //    estimate). Per accel sample, a_i is the GPS speed-trend in g. We remove
  //    a_i along the bike-forward axis expressed in the sensor frame.
  //
  //    DEVIATION FROM BRIEF (documented in task-1-report.md): the brief takes
  //    that forward axis as `R1ᵀ·(1,0,0)`. For z-up mounts (tilt > 90°, the
  //    real field-unit case) the *minimal* rotation over-rotates and flips
  //    that axis backward, so the correction doubles the bias instead of
  //    cancelling it, and a yawed mount is off by more than a sign. Instead we
  //    take the forward axis from the covariance of a_i with the sensor
  //    reading — Σ(a_i−ā)(s_i−m1) — which recovers direction *and* sign purely
  //    from data whenever the accel actually varies, gated by the existing
  //    MIN_FORWARD_FIT correlation ratio. When accel barely varies (e.g. a
  //    constant-bias segment) the covariance is undetermined and we fall back
  //    to the geometric `R1ᵀ·(1,0,0)`, which is correct in that regime because
  //    a credible constant-accel segment only occurs at low tilt.
  const forward1 = matVec(transpose(R1), [1, 0, 0])
  const gpsIdx = nearestGpsIndices(accelerometer, gpsPoints)
  const accelG: number[] = new Array(n).fill(0)
  if (gpsPoints.length > 0) {
    for (let i = 0; i < n; i++) {
      accelG[i] = (gpsSpeedTrendMs2(gpsPoints, gpsIdx[i]) ?? 0) / G
    }
  }
  let aBar = 0
  for (let i = 0; i < n; i++) aBar += accelG[i]
  aBar /= n
  let cov: Vec3 = [0, 0, 0]
  let corrDen = 0
  for (let i = 0; i < n; i++) {
    const da = accelG[i] - aBar
    cov = add(cov, scale(sub(samples[i], m1), da))
    corrDen += Math.abs(da) * norm(sub(samples[i], m1))
  }
  const covMag = norm(cov)
  const forward: Vec3 =
    corrDen > 1e-9 && covMag / corrDen >= SENSOR_CALIBRATION_MIN_FORWARD_FIT
      ? scale(cov, 1 / covMag)
      : forward1

  let m2: Vec3 = [0, 0, 0]
  for (let i = 0; i < n; i++)
    m2 = add(m2, sub(samples[i], scale(forward, accelG[i])))
  m2 = scale(m2, 1 / n)
  const mag2 = norm(m2)

  let gravityRotation = R1
  let gravityDir = u1
  if (mag2 >= SENSOR_CALIBRATION_MIN_MEAN_G) {
    gravityDir = scale(m2, 1 / mag2)
    gravityRotation = minimalRotation(gravityDir, DOWN)
  }

  // 5. Forward-axis (yaw) resolution. Rotate every sample by the gravity-only
  //    rotation, then correlate its horizontal projection with a_i to find the
  //    forward twist, and undo it with Rz(−yaw).
  let vx = 0
  let vy = 0
  let w = 0
  let dynamicCount = 0
  for (let i = 0; i < n; i++) {
    if (Math.abs(accelG[i] * G) < SENSOR_CALIBRATION_MIN_DYNAMIC_MS2) continue
    const s = matVec(gravityRotation, samples[i])
    vx += accelG[i] * s[0]
    vy += accelG[i] * s[1]
    w += Math.abs(accelG[i]) * Math.hypot(s[0], s[1])
    dynamicCount++
  }

  let rotation = gravityRotation
  let yawDeg = 0
  if (
    dynamicCount >= SENSOR_CALIBRATION_MIN_DYNAMIC_SAMPLES &&
    w > 0 &&
    Math.hypot(vx, vy) / w >= SENSOR_CALIBRATION_MIN_FORWARD_FIT
  ) {
    const yaw = Math.atan2(vy, vx)
    rotation = matMul(rotZ(-yaw), gravityRotation)
    yawDeg = (yaw * 180) / Math.PI
  }

  // 6. Roll about the bike x axis from the near-upright samples: their
  //    lateral mean in the bike frame is the roll error and nothing else.
  const upright = uprightMask(accelerometer, gpsPoints, gpsIdx)
  let lateral = 0
  let vertical = 0
  for (let i = 0; i < n; i++) {
    if (!upright[i]) continue
    const s = matVec(rotation, samples[i])
    lateral += s[1]
    vertical += s[2]
  }
  rotation = matMul(rotX(-Math.atan2(lateral, -vertical)), rotation)

  // 7. Outputs.
  const restDir = matVec(transpose(rotation), DOWN)
  const tiltDeg =
    (Math.acos(Math.max(-1, Math.min(1, dot(restDir, DOWN)))) * 180) / Math.PI
  return {
    rotation,
    trusted: true,
    tiltDeg,
    yawDeg,
    yawRefined: false,
    yawRefinementCorrelation: null,
  }
}

/**
 * Estimate the rotation that maps sensor-frame accelerometer readings into the
 * bike frame (x forward, y right, z down; at rest (0,0,−1)g), from a session's
 * accelerometer + GPS. Additive-honesty contract: every trust-gate failure
 * returns an identity rotation with trusted=false — never a guess. When a
 * gyroscope stream is supplied and the accelerometer estimate is trusted, the
 * yaw is refined against it; see refineYawWithGyro.
 */
export function estimateSensorFrameCalibration(
  accelerometer: AccelerometerReading[],
  gpsPoints: GpsPoint[],
  gyroscope?: GyroscopeReading[]
): SensorFrameCalibration {
  const calibration = estimateFromAccelerometer(accelerometer, gpsPoints)
  if (!gyroscope || !calibration.trusted) return calibration
  return refineYawWithGyro(calibration, gyroscope, gpsPoints)
}

function medianIntervalMs(gpsPoints: GpsPoint[]): number {
  const deltas: number[] = []
  for (let i = 1; i < gpsPoints.length; i++) {
    deltas.push(
      gpsPoints[i].timestamp.getTime() - gpsPoints[i - 1].timestamp.getTime()
    )
  }
  deltas.sort((a, b) => a - b)
  return deltas[Math.floor(deltas.length / 2)] ?? 0
}

function correlation(x: number[], y: number[], use: boolean[]): number {
  let sxy = 0
  let sxx = 0
  let syy = 0
  for (let i = 0; i < x.length; i++) {
    if (!use[i]) continue
    sxy += x[i] * y[i]
    sxx += x[i] * x[i]
    syy += y[i] * y[i]
  }
  return sxx && syy ? sxy / Math.sqrt(sxx * syy) : 0
}

/**
 * Yaw about the bike vertical that makes the gyro roll axis the axis
 * whose rate follows the GPS lean rate. The accelerometer fixes tilt
 * well and yaw loosely; a residual yaw leaks pitch rate into the roll
 * axis, which integrates as extra lean on one side. The yaw is the
 * direction, in the calibrated x–y plane, of the angular-rate component
 * that co-varies with the smoothed GPS lean rate. Unchanged
 * (yawRefined=false) when the calibration is untrusted, there is too
 * little riding, or the match at the refined yaw is below the gate;
 * yawRefinementCorrelation reports the match whenever it was measured.
 */
export function refineYawWithGyro(
  calibration: SensorFrameCalibration,
  gyroscope: GyroscopeReading[],
  gpsPoints: GpsPoint[]
): SensorFrameCalibration {
  if (!calibration.trusted || gyroscope.length === 0 || gpsPoints.length < 3)
    return calibration
  const intervalMs = medianIntervalMs(gpsPoints)
  if (intervalMs <= 0) return calibration
  const use = gpsPoints.map(
    (p) => p.speedKmh >= SENSOR_CALIBRATION_YAW_REFINE_MIN_SPEED_KMH
  )
  const ridingS = (use.filter(Boolean).length * intervalMs) / 1000
  if (ridingS < SENSOR_CALIBRATION_YAW_REFINE_MIN_RIDING_S) return calibration

  const smoothingSamples = Math.max(
    3,
    Math.round((SENSOR_CALIBRATION_YAW_REFINE_SMOOTHING_S * 1000) / intervalMs)
  )
  const gpsLean = smoothValuesGaussian(
    computeGpsCoordinatedLeanSeries(gpsPoints).map((v) =>
      Number.isFinite(v) ? v : 0
    ),
    smoothingSamples
  )
  const gpsLeanRate = gpsLean.map((_, i) => {
    if (i === 0 || i === gpsLean.length - 1) return 0
    const dt =
      (gpsPoints[i + 1].timestamp.getTime() -
        gpsPoints[i - 1].timestamp.getTime()) /
      1000
    return dt > 0 ? (gpsLean[i + 1] - gpsLean[i - 1]) / dt : 0
  })

  const ticks = reduceReadingsToGpsTicks(
    applySensorFrameCalibration(gyroscope, calibration),
    gpsPoints
  )
  const toDeg = (g: GyroscopeReading): number =>
    g.unit === 'rad/s' ? 180 / Math.PI : 1
  const rollRate = smoothValuesGaussian(
    ticks.map((g) => (g ? -g.x * toDeg(g) : 0)),
    smoothingSamples
  )
  const pitchRate = smoothValuesGaussian(
    ticks.map((g) => (g ? -g.y * toDeg(g) : 0)),
    smoothingSamples
  )

  let alongRoll = 0
  let alongPitch = 0
  for (let i = 0; i < gpsLeanRate.length; i++) {
    if (!use[i]) continue
    alongRoll += rollRate[i] * gpsLeanRate[i]
    alongPitch += pitchRate[i] * gpsLeanRate[i]
  }
  if (alongRoll === 0 && alongPitch === 0) return calibration
  const deltaRad = Math.atan2(alongPitch, alongRoll)
  const refinedLeanRate = rollRate.map(
    (r, i) => Math.cos(deltaRad) * r + Math.sin(deltaRad) * pitchRate[i]
  )
  const match = correlation(refinedLeanRate, gpsLeanRate, use)
  if (match < SENSOR_CALIBRATION_YAW_REFINE_MIN_CORRELATION) {
    return { ...calibration, yawRefinementCorrelation: match }
  }
  return {
    ...calibration,
    rotation: matMul(rotZ(-deltaRad), calibration.rotation),
    yawDeg: calibration.yawDeg + (deltaRad * 180) / Math.PI,
    yawRefined: true,
    yawRefinementCorrelation: match,
  }
}

function isIdentity(m: Mat3): boolean {
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++) if (m[i][j] !== (i === j ? 1 : 0)) return false
  return true
}

/**
 * Rotate x/y/z of each reading into the bike frame; every other field
 * (timestamp, unit, …) passes through untouched. Short-circuits and returns
 * the input array unchanged when the calibration rotation is identity.
 */
export function applySensorFrameCalibration<
  T extends { x: number; y: number; z: number },
>(readings: T[], calibration: SensorFrameCalibration): T[] {
  const R = calibration.rotation
  if (isIdentity(R)) return readings
  return readings.map((r) => {
    const v: Vec3 = [r.x, r.y, r.z]
    return {
      ...r,
      x: dot(R[0] as Vec3, v),
      y: dot(R[1] as Vec3, v),
      z: dot(R[2] as Vec3, v),
    }
  })
}
